import React from "react";

import { Helmet } from "react-helmet-async";

import { getAlternatesForPath } from "@/utils/i18nRoutes";

import i18n from "../i18n";

export const CANONICAL_ORIGIN = "https://pakville.ca";

const normalizePathname = (pathname: string): string => {
  if (pathname === "/" || pathname === "/fr/") return pathname;
  return pathname.replace(/\/+$/, "") || "/";
};

interface IDocumentAlternate {
  hreflang: string;
  href: string;
}

interface IDocumentProps {
  title: string;
  description?: string;
  // Path this page should declare as canonical, when it is served at more than
  // one URL (e.g. the legacy /gallery alias of /events). Drives both the
  // canonical link and the hreflang lookup, so every alias funnels to one URL.
  // Defaults to the current pathname.
  canonicalPath?: string;
  alternates?: IDocumentAlternate[];
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
  og?: {
    description: string;
    image: {
      src: string;
      type: string;
      width: number;
      height: number;
    };
  };
}

const DocumentComponent: React.FC<IDocumentProps> = ({
  title,
  description = undefined,
  canonicalPath = undefined,
  alternates = undefined,
  jsonLd = undefined,
  og = undefined,
}) => {
  const { t } = i18n;

  let pageTitle = "PakVille™";
  if (title !== "") {
    pageTitle += ` | ${title}`;
  } else {
    pageTitle = t("PakVille™ Modular Buildings");
  }

  const normalizedPath = normalizePathname(
    canonicalPath ?? window.location.pathname,
  );
  const canonicalUrl = `${CANONICAL_ORIGIN}${normalizedPath}`;
  const resolvedAlternates = alternates ?? getAlternatesForPath(normalizedPath);

  let metaDescription: string;
  if (description) {
    metaDescription = description;
  } else if (og?.description) {
    metaDescription = t(og.description);
  } else {
    metaDescription = pageTitle;
  }

  const ogImage = og
    ? `${CANONICAL_ORIGIN}/${og.image.src}?v=${import.meta.env.VITE_COMMIT_HASH || "4.0.1"}`
    : `${CANONICAL_ORIGIN}/images/models/pak-monaco/pakmonaco-og.jpg?v=${import.meta.env.VITE_COMMIT_HASH ?? "4.0.1"}`;
  const ogImageType = og?.image.type ?? "image/jpg";
  const ogImageWidth = og ? String(og.image.width) : "1200";
  const ogImageHeight = og ? String(og.image.height) : "630";

  return (
    <Helmet>
      <title>{pageTitle}</title>

      <meta content={metaDescription} name="description" />
      <meta content={pageTitle} property="og:title" />
      <meta content={metaDescription} property="og:description" />
      <meta content={canonicalUrl} property="og:url" />

      <meta content={ogImage} property="og:image" />
      <meta content={ogImageType} property="og:image:type" />
      <meta content={ogImageWidth} property="og:image:width" />
      <meta content={ogImageHeight} property="og:image:height" />
      <meta content={pageTitle} property="og:image:alt" />

      <link href={canonicalUrl} rel="canonical" />

      {resolvedAlternates?.map((alt) => (
        <link
          key={alt.hreflang}
          href={`${CANONICAL_ORIGIN}${normalizePathname(alt.href)}`}
          hrefLang={alt.hreflang}
          rel="alternate"
        />
      ))}

      {jsonLd ? (
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      ) : null}
    </Helmet>
  );
};

export default DocumentComponent;
